/* eslint-disable no-unused-vars */
import * as d3 from 'd3';
import _ from 'lodash';
import { intcomma, apmonth, apdate } from 'journalize';
import {
  customDateFormat,
  customMobileDateFormat,
  customPercFormat,
  getLabeledData,
} from '../utils/charts-utils';
import * as moment from 'moment';
// import { swoopyDrag } from '../swoopy-drag';
import { wordwrap } from 'd3-jetpack';

import data from '../../../data/daily_tables/all_tables.json';
import colors from '../../../data/chart-colors.json';

// a reference to the default graphic container, change if needed
const container = d3.select('#positive-rate');

// a helper function to grab the container's width
const getFrameWidth = () => container.node().offsetWidth;

//set margins
let margin = {
  top: 15,
  left: 40,
  bottom: 40,
};

let breakPoint = 500;

// grabbing totals
let dataTotals = [];

// NOTE: THIS IS TEMPORARILY ADDED
// to account for them not having testing data on 5/24
// data['cases_latest_date'] = '08_23_2020';

// If we have incomplete data for the yesterday's date, let's use the day before
// yesterday's date is what we use for the positivity rate
let cases_latest_date = data['cases_latest_date'];

let yesterday = moment(data['cases_latest_date'], 'M_D_YYYY')
  .subtract(1, 'days')
  .format('MM_DD_YYYY');
let yesterday_incomplete = data[yesterday].statewide_data.incomplete;

if (yesterday_incomplete != undefined) {
  if (
    yesterday_incomplete.includes('tests_diff') ||
    yesterday_incomplete.includes('case_dif')
  ) {
    cases_latest_date = yesterday;
  }
}

// Find the positive rate which is the ratio of positive cases for that day to the tests conducted that day
// there are six properties prior that we want to exclude: 'latest_date', 'yesterday_date', 'week_ago', 'cases_latest_date', 'cases_yesterday_date', 'cases_week_ago', 'cases_yesterday_date'
for (let i = Object.keys(data).length - 1; i > 6; i--) {
  if (
    moment(Object.keys(data)[i], 'M_D_YYYY').isBefore(
      moment(cases_latest_date, 'M_D_YYYY')
    )
  ) {
    dataTotals.push({
      incomplete: data[Object.keys(data)[i - 1]].statewide_data.incomplete,
      unformatted_date: Object.keys(data)[i - 1],
      positive_rate:
        data[Object.keys(data)[i - 1]].statewide_data.specimen_positivity_rate,
    });
  }
}

// any days that had missing data
let missedDates = dataTotals.filter(d => {
  if (d.positive_rate == 'no_data') {
    return true;
  }
});

// changing to moment date+time objects
dataTotals.forEach(d => (d.date = moment(d.unformatted_date, 'M_D_YYYY')));

// Get the new starting date because the state did not provide comprehensive deaths data before then
const start_date = dataTotals.filter(d => d.unformatted_date == '04_01_2020');

// Filter out everything before the new starting date
dataTotals = dataTotals.filter(d => d.date >= start_date[0].date);
const dataTotals2 = dataTotals.filter(d => {
  return (
    d.date >= start_date[0].date &&
    !missedDates.map(d => d.date).includes(d.date) // filter out any dates that have missing test data
  );
})

// Checks to see if the latest date we have is also missing the positivity rate
// if so, filter it out so we use the previous date as the latest date
var latest_date = dataTotals[dataTotals.length - 1].date;

if (missedDates.map(d => d.date).includes(latest_date)) {
  dataTotals = dataTotals2;
}


// sort the data from earliest date to latest data
dataTotals.sort((a, b) => (b.date.isBefore(a.date) ? 1 : -1));

// slice the latest date off because positive rate lags a day behind
dataTotals = dataTotals.slice(0, -1);

// changing to moment date+time objects
dataTotals.forEach(d => (d.date = moment(d.unformatted_date, 'M_D_YYYY')));

// Add an id based on index to use for joining
dataTotals.forEach((d, i) => (d.id = i));

// grabbing totals and ranges
let dateRange = d3.extent(dataTotals, d => d.date),
  totalsMaxAvg = d3.max(dataTotals, d => d.positive_rate);

// set ranges
let x = d3.scaleTime().domain(dateRange);
let y = d3
  .scaleLinear()
  .domain([0, 40])
  .nice();

// append svg
let svg = container.append('svg'),
  g = svg.append('g').attr('class', 'line-chart'),
  labels_g = svg.append('g').attr('class', 'labels-container');

let xAxis = g.append('g').attr('class', 'axis axis--x'),
  yAxis = g.append('g').attr('class', 'axis axis--y');

// Get the new starting date for us to switch out to molecular tests, which is about 7 days after they started reporting it on May 13
const start_for_7_day_avg = dataTotals.filter(
  d => d.unformatted_date == '05_20_2020'
);

let line = d3
  .line()
  .defined(d => !isNaN(d.positive_rate))
  .x(d => x(d.date))
  .y(function(d) {
    return y(d.positive_rate);
  });

// Note: DSHS changed its viral tests numbers for June 6 so that the total number of viral tests actually decreased instead of increased giving us a negative daily viral tests and completely screwing up the positive rate by giving us a negative positive rate so we have to make it not visible.
// Note: DSHS started calculating their seven-day average using just molecular tests on August 9. We've adjusted this to reflect that change.

let missedDatesArea = g
  .selectAll('.missing-dates')
  .data(missedDates)
  .enter()
  .append('rect')
  .attr('class', 'missing-dates')
  .attr('fill', d => colors.bar_neutral)
  .attr('opacity', 0.5);

let dateReopening = moment('5_01_2020', 'M_D_YYYY');
let dateMaskOrder = moment('7_02_2020', 'M_D_YYYY');

// filter out days with no moving average
let dataTotalsNoMovingAvgSubtract = dataTotals.filter(
  d => !isNaN(d.positive_rate)
);

let pathOutline = g
  .append('path')
  .data([dataTotalsNoMovingAvgSubtract])
  .attr('class', 'line')
  .attr('fill', 'none')
  .attr('stroke', '#fff')
  .attr('stroke-width', 4);

let path = g
  .append('path')
  .data([dataTotalsNoMovingAvgSubtract])
  .attr('class', 'line')
  .attr('fill', 'none')
  .attr('stroke', colors.line_moving_avg)
  .attr('stroke-width', 2);

let reopeningGroup = g.append('g').attr('class', 'reopening-line');
let reopeningLine = reopeningGroup
  .append('line')
  .attr('x1', 0)
  .attr('x2', 0)
  .attr('y1', 0)
  .attr('stroke', '#797979')
  .attr('stroke-width', '1px')
  .attr('stroke-dasharray', '3px');
let reopeningText = reopeningGroup.append('text');

let maskorderGroup = g.append('g').attr('class', 'reopening-line');
let maskorderLine = maskorderGroup
  .append('line')
  .attr('x1', 0)
  .attr('x2', 0)
  .attr('y1', 0)
  .attr('stroke', '#797979')
  .attr('stroke-width', '1px')
  .attr('stroke-dasharray', '3px');
let maskorderText = maskorderGroup.append('text');

const day_for_label = [[dataTotals][0][6]]; // Gets the day that where the 7-day average label will be next to

let moving_avg_label = g.append('g').attr('class', 'moving-avg-label');

let moving_avg_g = moving_avg_label
  .selectAll('g')
  .data(day_for_label)
  .enter()
  .append('g');

let moving_avgText = moving_avg_g.append('g').attr('class', 'label-text');

moving_avgText
  .append('text')
  .text('7-day average')
  .attr('fill', colors.line_moving_avg)
  .attr('font-weight', 700);

let labels = labels_g.append('g').attr('class', 'labeled-elements');

let labelElements = labels
  .selectAll('g')
  .data(getLabeledData(dataTotals, ['']))
  .enter()
  .append('g')
  .attr('class', 'label-element');

let labelText = labelElements.append('g').attr('class', 'label-text');

// labelText
//   .append('text')
//   .text((d, i) => {
//     if (i === 0) {
//       let month = apmonth(new Date(d.date)),
//         day = d.date.format('D');

//       return `${month} ${day}`;
//     } else return '';
//   })
//   .attr('font-weight', 700);

// let casesLabel = labelText
//   .append('text')
//   .attr('fill', colors.line_moving_avg)
//   .text(d => {
//     return `${d3.format('.3')(d.positive_rate)}%`;
//   });

let events = g.append('g').attr('class', 'events');

let eventGroups = events
  .selectAll('g')
  .data([
    {
      event_desktop: 'Goal: Below 6%',
      event_mobile: 'Goal: Below 6%',
      rate_threshold: 6,
    },
  ])
  .enter()
  .append('g');

let eventText = eventGroups.append('text');

let eventLines = eventGroups
  .append('line')
  .attr('stroke', '#787878')
  .attr('stroke-width', '1px')
  .attr('stroke-dasharray', 5);

function resize() {
  let frameWidth = getFrameWidth(),
    frameHeight;

  if (frameWidth < breakPoint) {
    frameHeight = 275;
    margin.right = 60;
  } else {
    frameHeight = 325;
    margin.right = 65;
  }

  let chartWidth = frameWidth - margin.left,
    chartHeight = frameHeight - margin.bottom;

  svg.attr('width', frameWidth).attr('height', frameHeight);
  g.attr('width', chartWidth).attr(
    'transform',
    `translate(${margin.left},${margin.top})`
  );
  labels_g.attr('transform', `translate(${margin.left},${margin.top})`);

  let barWidth = chartWidth / dataTotals.length;

  x.range([barWidth / 1.5, chartWidth - barWidth / 1.5]);
  y.range([chartHeight, 0]);

  xAxis
    .call(
      d3
        .axisBottom(x)
        .ticks(15)
        .tickFormat((d, i) => {
          return frameWidth < breakPoint
            ? customMobileDateFormat(d, i)
            : customDateFormat(d, i, 4);
        })
    )
    .attr('transform', `translate(0,${frameHeight - margin.bottom})`);

  yAxis.call(
    d3
      .axisLeft(y)
      .ticks(5)
      .tickSize(-chartWidth)
      .tickFormat(customPercFormat)
  );

  missedDatesArea
    .attr('width', barWidth) // to prevent a slight overlap
    .attr('x', d => x(d.date) - barWidth / 2 - barWidth / 5 / 2) // space between bars is 1.5 so we shift this area half of that
    .attr('y', 0)
    .attr('height', chartHeight);

  pathOutline.attr('d', line);
  path.attr('d', line);

  moving_avg_g
    .attr('transform', d => `translate(${x(d.date)},${y(d.positive_rate)})`)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

  moving_avgText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'translate(0, -14)';
      } else return 'translate(0, -14)';
    })
    .attr('text-anchor', 'start');

  labelElements
    .attr('transform', d => `translate(${x(d.date)},${y(d.positive_rate)})`)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

  // labelText
  //   .attr('transform', (d, i) => {
  //     if (i === 0 && frameWidth < breakPoint) {
  //       return 'translate(15, 0)';
  //     } else return 'translate(15, 0)';
  //   })
  //   .attr('text-anchor', 'start');

  // casesLabel.attr('dy', frameWidth < breakPoint ? 16 : 17);

  eventGroups.attr('transform', d => `translate(0,${y(d.rate_threshold)})`);

  eventLines.attr('x1', 0).attr('x2', chartWidth);

  eventText
    .attr('class', 'label-text font-weight-bold')
    .text(d => (frameWidth < breakPoint ? d.event_mobile : d.event_desktop))
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px')
    .attr('dx', frameWidth < breakPoint ? 5 : 10)
    .attr('dy', frameWidth < breakPoint ? 16 : 18);

  reopeningGroup.attr('transform', `translate(${x(dateReopening)}, 0)`);
  reopeningLine.attr('y2', chartHeight);
  reopeningText
    .attr('y', frameWidth < breakPoint ? 25 : 92)
    .text('')
    .tspans(wordwrap('Reopening began May 1', 15))
    .attr('dx', 5)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

  maskorderGroup.attr('transform', `translate(${x(dateMaskOrder)}, 0)`);
  maskorderLine.attr('y2', chartHeight);
  maskorderText
    .attr('y', frameWidth < breakPoint ? 80 : 92)
    .attr('text-anchor', 'start')
    .text('')
    .tspans(wordwrap('Mask order issued July 2', 15))
    .attr('dx', 5)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');
}

// call resize on load
resize();
window.addEventListener('resize', _.debounce(resize, 200));

// OUTSIDE OF RESIZE()

// let metaInfo = g
//   .append('g')
//   .attr('class', 'meta')
//   .attr('font-size', '12px')
//   .attr('fill', 'hsl(0, 0%, 47%)');

// this is the same function findChartMeta() in nunjucks.js
// find a way to import that function here
// metaInfo
//   .append('text')
//   .text(`Note: ${data.Meta.find(d => d.Chart === 'Line').Note}`)
//   .attr('y', 45);

// metaInfo
//   .append('text')
//   .html(
//     `Source: <a style="fill: hsl(0, 0%, 47%); text-decoration: underline;"href="https://www.cbp.gov/sites/default/files/assets/documents/2019-Mar/bp-total-monthly-apps-sector-area-fy2018.pdf">${
//       data.Meta.find(d => d.Chart === 'Line').Source
//     }</a>`
//   )
//   .attr('y', 65);

// arrow marker, need to append to the svg first to use
// svg
//   .append('marker')
//   .attr('id', 'arrow')
//   .attr('viewBox', '-10 -10 20 20')
//   .attr('markerWidth', 10)
//   .attr('markerHeight', 10)
//   .attr('fill', 'hsl(0, 0%, 29%)')
//   .attr('orient', 'auto-start-reverse')
//   .append('path')
//   .attr('d', 'M-6.75,-6.75 L 0,0 L -6.75,6.75');

// let swoopySel = svg.append('g').attr('class', 'swoopy-drag-lines');

// // annotations for swoopyDrag
// let annotations = [
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: 'M-25,-19C-32,-38,-47,-49,-63,-50',
//     text: 'March 24',
//     weight: 700,
//     textOffset: [-186, -77],
//   },
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: '',
//     text: 'State changed how it reports numbers.',
//     weight: 400,
//     textOffset: [-186, -62],
//   },
// ];

// let mobileAnnotations = [
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: 'M-16,-13C-30,-13,-42,-24,-46,-39',
//     text: 'March 24',
//     weight: 700,
//     textOffset: [-95, -76],
//   },
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: '',
//     text: 'State changed how it reports numbers.',
//     weight: 400,
//     textOffset: [-95, -62],
//   },
// ];

// INSIDE OF RESIZE()

// metaInfo.attr('transform', `translate(0, ${chartHeight})`);

// let swoopy = swoopyDrag()
//   .x(d => x(d.xVal) + 10)
//   .y(d => y(d.yVal))
//   .draggable(false)
//   .annotations(frameWidth < breakPoint ? mobileAnnotations : annotations);

// swoopySel
//   .attr('transform', `translate(${margin.left},${margin.top})`)
//   .call(swoopy)
//   .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

// swoopySel.selectAll('text').each(function(d) {
//   d3.select(this)
//     .text('')
//     .attr('font-weight', d.weight)
//     .tspans(wordwrap(d.text, frameWidth < breakPoint ? 24 : 24));
// });

// swoopySel.selectAll('path').attr('marker-start', 'url(#arrow)');
